import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FieldDefinitionsService from "../api/FieldDefinitionsService";
import CustomSnackbar from "../components/common/CustomSnackbar";
import { MDBContainer } from "mdb-react-ui-kit";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const AdminFieldDefinitionsPage = () => {
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isLoading = useSelector((state) => state.user.isLoading);
  const navigate = useNavigate();

  const [fieldDefinition, setFieldDefinition] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [fieldDefinitionToEdit, setFieldDefinitionToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldDefinitionToDelete, setFieldDefinitionToDelete] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      loadData();
    }
    if (!isLoading && !isLoggedIn) {
      navigate("/login");
    }
  }, [isLoading, isLoggedIn]);

  if (!isLoading && !isLoggedIn) {
    return <div>Loading...</div>;
  }

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await FieldDefinitionsService.getAll();
      setFieldDefinition(response.data);
    } catch (e) {
      setSnackbar({ open: true, message: "Помилка: " + e.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (updated) => {
    if (!updated.name?.trim()) {
      setShowValidation(true);
      setSnackbar({ open: true, message: "Поле 'Назва' є обовʼязковим.", severity: "error" });
      return;
    }
    if (!updated.systemName?.trim()) {
      setShowValidation(true);
      setSnackbar({ open: true, message: "Поле 'Системна назва' є обовʼязковим.", severity: "error" });
      return;
    }
    if (!updated.fieldType?.trim()) {
      setShowValidation(true);
      setSnackbar({ open: true, message: "Поле 'Тип поля' є обовʼязковим.", severity: "error" });
      return;
    }
    try {
      if (updated.id) await FieldDefinitionsService.update(updated);
      else await FieldDefinitionsService.create(updated);
      setSnackbar({ open: true, message: "Збережено!", severity: "success" });
      setEditDialogOpen(false);
      loadData();
    } catch (e) {
      setSnackbar({ open: true, message: "Помилка збереження: " + e.message, severity: "error" });
    }
  };

  const promptDelete = (cat) => {
    setFieldDefinitionToDelete(cat);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await FieldDefinitionsService.deleteById(fieldDefinitionToDelete.id);
      setSnackbar({ open: true, message: "Видалено!", severity: "success" });
      setDeleteDialogOpen(false);
      setFieldDefinitionToDelete(null);
      setFieldDefinition((prev) => prev.filter((c) => c.id !== fieldDefinitionToDelete.id));
    } catch (e) {
      setSnackbar({ open: true, message: "Помилка видалення: " + e.message, severity: "error" });
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "name", headerName: "Назва", flex: 1 },
    { field: "systemName", headerName: "Системна назва", flex: 2 },
    { field: "fieldType", headerName: "Тип поля", flex: 1 },
    {
      field: "actions", type: "actions", headerName: "Дії", width: 120,
      getActions: (params) => {
        if (params.row.systemName === "document_name") {
          return [];
        }
        return [
          <GridActionsCellItem
            icon={<EditIcon/>} label="Редагувати"
            onClick={() => {
              setFieldDefinitionToEdit({ ...params.row, documentTypeMap: params.row.documentTypeMap || {} });
              setEditDialogOpen(true);
            }}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon/>} label="Видалити"
            onClick={() => promptDelete(params.row)}
          />
        ]
      }
    }
  ];

  return (
    <Box p={2} marginLeft={5} marginRight={5}>
      <MDBContainer className="d-flex justify-content-center align-items-center text-center">
        <Typography variant="h5" mb={2} marginRight={20}>Поля карток</Typography>
        <Button
          startIcon={<AddIcon/>} variant="contained"
          sx={{ mb: 2 }}
          onClick={() => {
            setFieldDefinitionToEdit({ name: "", systemName: "", fieldType: "" });
            setEditDialogOpen(true);
            setShowValidation(false);
          }}
        >
          Додати поле
        </Button>
      </MDBContainer>

      {loading ? <CircularProgress/> : (
        <DataGrid
          rows={fieldDefinition}
          columns={columns}
          autoHeight
          pageSize={10}
          rowsPerPageOptions={[10]}
          getRowId={(row) => row.id}
          rowSelection={false}
          showToolbar={true}
        />
      )}

      {/* ✏️ Діалог створення / редагування */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{fieldDefinitionToEdit?.id ? "Редагування поля для картки" : "Створення поля для картки"}</DialogTitle>
        <DialogContent>
          <TextField
            required fullWidth margin="dense" label="Назва"
            value={fieldDefinitionToEdit?.name || ""}
            onChange={(e) => {
              setFieldDefinitionToEdit((c) => ({ ...c, name: e.target.value }));
              setShowValidation(false);
            }}
            error={showValidation && !fieldDefinitionToEdit?.name?.trim()}
            helperText={showValidation && !fieldDefinitionToEdit?.name?.trim() ? "Назва обовʼязкова" : ""}
          />
          <TextField
            required fullWidth margin="dense" label="Системна назва"
            value={fieldDefinitionToEdit?.systemName || ""}
            onChange={(e) => {
              setFieldDefinitionToEdit((c) => ({ ...c, systemName: e.target.value }));
              setShowValidation(false);
            }}
            error={showValidation && !fieldDefinitionToEdit?.systemName?.trim()}
            helperText={showValidation && !fieldDefinitionToEdit?.systemName?.trim() ? "Систмена назва обовʼязкова" : ""}
          />
          <TextField
            required select fullWidth margin="dense" label="Тип поля"
            value={fieldDefinitionToEdit?.fieldType || ""}
            onChange={(e) => setFieldDefinitionToEdit((c) => ({ ...c, fieldType: e.target.value }))}
            SelectProps={{ native: true }}
            error={showValidation && !fieldDefinitionToEdit?.fieldType?.trim()}
            helperText={showValidation && !fieldDefinitionToEdit?.fieldType?.trim() ? "Тип поля обовʼязковий" : ""}
          >
            <option value=""></option>
            <option value="TEXT">TEXT</option>
            <option value="PRICE">PRICE</option>
            <option value="DATE">DATE</option>
            <option value="COMMENT">COMMENT</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" color="success" onClick={() => handleSave(fieldDefinitionToEdit)}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🗑️ Підтвердження видалення */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Підтвердження видалення</DialogTitle>
        <DialogContent>
          <Typography>Впевнені, що хочете видалити <strong>{fieldDefinitionToDelete?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Видалити</Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default AdminFieldDefinitionsPage;
